<template>
  <div class="rd-screen">
    <report-tools :copy="shownRows.length > 0" @copy="copyTable">
      <label class="rd-btn rd-btn--sm rd-toggle" :class="{ 'is-on': includeBranches }">
        <input v-model="includeBranches" type="checkbox" class="rd-visually-hidden" />
        <v-icon size="16" :color="includeBranches ? '#9a3412' : '#5f5852'">
          {{ includeBranches ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
        </v-icon>
        {{ $t("more_restaurants_report.whole_company") }}
      </label>
    </report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!shownRows.length"
      icon="mdi-account-off-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl mr-items">
        <thead>
          <tr>
            <th scope="col">{{ $t("worker") }}</th>
            <th scope="col" class="rd-col-time">{{ $t("date") }}</th>
            <th scope="col" class="is-center">{{ $t("shifts") }}</th>
            <th scope="col">{{ $t("more_restaurants_report.item") }}</th>
            <th scope="col" class="is-right">{{ $t("more_restaurants_report.quantity_short") }}</th>
            <th scope="col">{{ $t("more_restaurants_report.package_items") }}</th>
            <th v-if="showAmount" scope="col" class="is-right">{{ $t("amount") }}</th>
            <th scope="col" class="is-right rd-col-taken">{{ $t("taken") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in shownRows" :key="row.rowKey" :class="{ 'mr-items__first': row.showWorker }">
            <td class="is-name" :title="row.worker">
              <span v-if="row.showWorker" class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.worker }}</span>
              </span>
            </td>
            <td class="rd-col-time rd-num rd-muted">{{ row.dateText }}</td>
            <td class="is-center">
              <report-shift-tag v-if="row.shiftNumber" :shift="row.shiftNumber"></report-shift-tag>
              <span v-else class="rd-dash" aria-hidden="true">–</span>
            </td>
            <td class="is-name" :title="row.itemText">{{ row.itemText }}</td>
            <td class="is-right rd-num">{{ row.quantity }}</td>
            <td class="rd-muted mr-items__combo">{{ row.comboText }}</td>
            <td v-if="showAmount" class="is-right rd-num">{{ row.amount }}</td>
            <td class="is-right rd-num rd-col-taken" :class="{ 'is-zero': !row.quantity_payed }">{{ row.quantity_payed }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>{{ $t("total") }}</td>
            <td class="rd-col-time"></td>
            <td></td>
            <td></td>
            <td class="is-right rd-num">{{ sums.quantity }}</td>
            <td></td>
            <td v-if="showAmount" class="is-right rd-num">{{ sums.amount }}</td>
            <td class="is-right rd-num rd-col-taken">{{ sums.quantity_payed }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import ReportMealsTabEmployeeItems from "@/components/report/more-restaurants/tabs/ReportMealsTabEmployeeItems.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMoreWorkerItemRows from "../../../shared/more/hasMoreWorkerItemRows";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Radnici – po stavkama (firme "više restorana") na računaru: ko je šta naručio po danu i smeni (paket / obrok /
 * jelo, količina, jela u paketu, preuzeto), ime radnika samo u prvom redu. Firmu, smene i pretragu čita iz
 * zajedničke trake, a "Cela firma" (sa filijalama) je u alatima. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopMoreWorkerItems",
  extends: ReportMealsTabEmployeeItems,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMoreWorkerItemRows],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "quantity", label: this.$t("report_desktop.total_meals"), value: this.sums.quantity, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.quantity_payed },
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.workerCount },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.shownRows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    async copyTable() {
      const headers = [
        this.$t("worker"),
        this.$t("date"),
        this.$t("shifts"),
        this.$t("more_restaurants_report.item"),
        this.$t("more_restaurants_report.quantity_short"),
        this.$t("more_restaurants_report.package_items"),
        ...(this.showAmount ? [this.$t("amount")] : []),
        this.$t("taken"),
      ];
      const rows = this.shownRows.map((row) => [
        row.worker,
        row.dateText,
        row.shiftNumber || "",
        row.itemText,
        row.quantity,
        row.comboText,
        ...(this.showAmount ? [row.amount] : []),
        row.quantity_payed,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
